import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  Applications,
  WhyChooseLiftile,
  QualitySafetyCommitment,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
} from "../components/product";
import {
  Award,
  BadgeCheck,
  Building2,
  Circle,
  CircleDot,
  ClipboardCheck,
  Factory,
  Flag,
  Frame,
  Grip,
  HardHat,
  Home,
  Landmark,
  Layers,
  Leaf,
  Move,
  Package,
  Route,
  Scale,
  ShieldCheck,
  Sparkles,
  Timer,
  Truck,
  Warehouse,
  Wrench,
} from "lucide-react";

/* =====================================================================
   WHEEL BARROW (LT Series — LT1X / LT2X / LT3X) — dedicated product
   page. Composed entirely from /components/product/* shared primitives.
   Only product-specific data lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/product-wheel-barrow.jpg";

const HERO_SPECS = [
  { Icon: Package, value: "3.5 cu.ft", label: "Tray Capacity" },
  { Icon: CircleDot, value: "Single & Dual", label: "Premium Wheels" },
  { Icon: Grip, value: "Ergonomic", label: "Rubber Grip Handles" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "Heavy Duty Construction",
  "Premium Branded Tires",
  "Owner Inspected",
  "Made in India",
];

const GALLERY = [
  {
    src: "/wheel-barrow-lt1x.png",
    caption:
      "LIFTILE LT1X Single Wheel Barrow — compact material transport for tight sites",
  },
  {
    src: "/wheel-barrow-lt2x.png",
    caption:
      "LIFTILE LT2X Double Wheel Barrow — heavy-duty twin-wheel stability on rough ground",
  },
];

const QUICK_SPECS = [
  { label: "Tray Capacity", value: "3.5 Cubic Feet" },
  { label: "Wheel Options", value: "Single / Heavy / Dual" },
  { label: "Handle", value: "Rubber Grip" },
  { label: "Construction", value: "Heavy Duty Steel" },
];

const MODELS = {
  LT1X: {
    name: "LT1X",
    tagline:
      "Compact single-wheel barrow for tight sites and everyday material handling",
    rows: [
      { label: "Capacity", value: "3.5 Cubic Feet" },
      { label: "Wheel Type", value: "Single Premium Wheel" },
      { label: "Handle", value: "Rubber Grip" },
      { label: "Construction", value: "Heavy Duty Steel" },
      { label: "Best For", value: "Compact Material Handling" },
    ],
  },
  LT2X: {
    name: "LT2X",
    tagline:
      "Versatile heavy-duty barrow — the everyday workhorse for general site work",
    rows: [
      { label: "Capacity", value: "3.5 Cubic Feet" },
      { label: "Wheel Type", value: "Heavy Duty Premium Wheel" },
      { label: "Handle", value: "Ergonomic Rubber Grip" },
      { label: "Construction", value: "Heavy Duty Fabricated Steel" },
      { label: "Best For", value: "General Construction Work" },
    ],
  },
  LT3X: {
    name: "LT3X",
    tagline:
      "Extra heavy-duty dual-wheel barrow for maximum stability and heavy loads",
    rows: [
      { label: "Capacity", value: "3.5 Cubic Feet" },
      { label: "Wheel Type", value: "Dual Premium Wheels" },
      { label: "Handle", value: "Heavy Duty Rubber Grip" },
      { label: "Construction", value: "Extra Heavy Duty Steel" },
      { label: "Best For", value: "Maximum Stability & Heavy Loads" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Layers,
    title: "Heavy Duty Steel Construction",
    desc: "Precision-formed heavy-gauge steel tray and chassis engineered for demanding site conditions and years of daily use.",
  },
  {
    Icon: CircleDot,
    title: "Premium Branded Tires",
    desc: "Trusted branded pneumatic tyres deliver smooth rolling, long service life and reliable performance across rough site terrain.",
  },
  {
    Icon: Grip,
    title: "Ergonomic Rubber Grip Handles",
    desc: "Anti-slip rubber grip handles reduce operator fatigue and deliver a secure, comfortable hold during long site shifts.",
  },
  {
    Icon: Frame,
    title: "Heavy Duty Welded Frame",
    desc: "Precision-welded frame delivers rigidity, structural integrity and dependable load-bearing performance under full capacity.",
  },
  {
    Icon: Sparkles,
    title: "Powder Coated Finish",
    desc: "Premium powder coating delivers a site-grade corrosion-resistant finish and a professional, long-lasting appearance.",
  },
  {
    Icon: Move,
    title: "Smooth Material Transportation",
    desc: "Balanced geometry and premium wheels deliver smooth, low-effort transport of cement, bricks, aggregates and site materials.",
  },
  {
    Icon: Scale,
    title: "Balanced Load Distribution",
    desc: "Engineered tray and wheel positioning distribute load evenly for stable handling — even with heavy or awkward payloads.",
  },
  {
    Icon: ShieldCheck,
    title: "Corrosion Resistant Surface",
    desc: "Powder-coated finish over a rust-resistant substrate keeps the barrow looking and performing well through wet-site conditions.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance Design",
    desc: "Simple, service-friendly mechanical design with easy access to wheel, axle and handle components keeps downtime low.",
  },
  {
    Icon: Route,
    title: "Easy Maneuverability",
    desc: "Optimized wheel geometry and handle ergonomics make the LT Series easy to steer through tight spaces and busy sites.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LT Series barrow is personally inspected and quality signed off before it leaves the LIFTILE factory.",
  },
  {
    Icon: BadgeCheck,
    title: "Built for Daily Site Use",
    desc: "Engineered for continuous daily use — dependable load after load, project after project, on demanding construction sites.",
  },
];

const APPLICATIONS = [
  {
    Icon: Home,
    title: "Residential Construction",
    desc: "Reliable daily material transport for individual homes, villas, apartments and residential tower projects.",
  },
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "Dependable site handling for offices, malls, hotels, hospitals and mixed-use commercial developments.",
  },
  {
    Icon: Factory,
    title: "Industrial Construction",
    desc: "Robust performance on plants, factories, warehouses and heavy industrial construction sites.",
  },
  {
    Icon: Landmark,
    title: "Infrastructure Projects",
    desc: "Trusted material transport for metros, treatment plants, power plants, ports and public infrastructure works.",
  },
  {
    Icon: Warehouse,
    title: "Warehouse Material Handling",
    desc: "Dependable everyday material handling for warehouses, godowns, storage yards and distribution centres.",
  },
  {
    Icon: Route,
    title: "Road Construction",
    desc: "Reliable material transport for concrete roads, kerbs, drains, expressways and highway construction works.",
  },
  {
    Icon: Leaf,
    title: "Landscaping",
    desc: "Smooth material movement for landscaping projects — soil, gravel, paving stones, plants and site debris.",
  },
  {
    Icon: Truck,
    title: "General Site Material Transportation",
    desc: "Everyday transport of cement, bricks, sand, aggregate, tools and general site materials across every project.",
  },
];

const TRUST_CARDS = [
  {
    Icon: HardHat,
    title: "Heavy Duty Construction",
    desc: "Precision-welded steel construction built for demanding site conditions and continuous daily site use.",
  },
  {
    Icon: CircleDot,
    title: "Premium Wheels",
    desc: "Trusted branded pneumatic wheels deliver smooth rolling, long service life and dependable site performance.",
  },
  {
    Icon: ShieldCheck,
    title: "Reliable Performance",
    desc: "Load after load, project after project — engineered for dependable everyday material handling.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected",
    desc: "Every LT Series barrow is personally inspected and quality signed off before dispatch.",
  },
  {
    Icon: Wrench,
    title: "Low Maintenance",
    desc: "Simple, service-friendly mechanical design keeps downtime and operating costs low across years of use.",
  },
  {
    Icon: Flag,
    title: "Made in India",
    desc: "Designed and manufactured in India for Indian construction site conditions and after-sales support.",
  },
  {
    Icon: Grip,
    title: "Easy Handling",
    desc: "Ergonomic rubber grip handles and balanced geometry deliver low-effort, low-fatigue site material movement.",
  },
];

const TECH_HIGHLIGHTS = [
  {
    Icon: Frame,
    title: "Heavy Duty Frame",
    desc: "Precision-welded heavy-gauge steel frame with rigid load-bearing geometry for stable, dependable performance.",
  },
  {
    Icon: Award,
    title: "Premium Tire Quality",
    desc: "Trusted branded pneumatic tyres — single, heavy-duty or dual configuration — for smooth rolling on rough site terrain.",
  },
  {
    Icon: Scale,
    title: "Balanced Design",
    desc: "Engineered tray and wheel positioning distribute load evenly for stable, low-effort handling under full capacity.",
  },
  {
    Icon: Move,
    title: "Smooth Transportation",
    desc: "Optimized wheel geometry and ergonomic handles deliver smooth, controlled movement across every site surface.",
  },
  {
    Icon: Layers,
    title: "Durable Construction",
    desc: "Powder-coated finish over heavy-gauge steel resists corrosion, impact and daily site abuse.",
  },
  {
    Icon: Timer,
    title: "Long Service Life",
    desc: "Premium materials, powder-coated protection and quality wheels ensure years of dependable everyday use.",
  },
];

const FAQS = [
  {
    q: "What is the load capacity of the LT Series?",
    a: "Every LT Series Wheel Barrow features a 3.5 cubic feet tray capacity — sized for the most common site loads including cement, bricks, sand, aggregate and general construction materials.\n\nThe LT3X extra heavy-duty dual-wheel version is engineered to safely handle heavier and denser payloads with maximum stability.",
  },
  {
    q: "What is the difference between LT1X, LT2X and LT3X?",
    a: "All three models share the same 3.5 cu.ft tray capacity, but differ in wheel configuration and structural build.\n\nLT1X — Compact single-wheel barrow for tight sites and everyday material handling.\n\nLT2X — Heavy-duty single premium wheel with ergonomic rubber grip — the everyday workhorse for general construction work.\n\nLT3X — Extra heavy-duty dual premium wheels with heavy-duty rubber grip — engineered for maximum stability and heavy, awkward loads.",
  },
  {
    q: "What construction material is used in the LT Series?",
    a: "The LT Series is fabricated from heavy-gauge steel with precision-welded joints and a premium powder-coated finish over a rust-resistant substrate.\n\nLT3X uses an extra heavy-duty steel gauge for maximum load-bearing capacity and long service life on the toughest sites.",
  },
  {
    q: "Which construction sites is the LT Series suitable for?",
    a: "The LT Series is engineered for a wide range of construction environments including residential construction, commercial buildings, industrial construction, infrastructure projects, warehouse material handling, road construction, landscaping and general site material transportation.",
  },
  {
    q: "What maintenance does the LT Series require?",
    a: "The LT Series is a very low-maintenance product. Routine tasks include periodic tyre pressure checks, inspection of wheel bearings, occasional lubrication of the wheel axle and general cleaning of the tray after concrete or mortar handling.\n\nThe powder-coated finish will resist site corrosion for years with only basic cleaning care.",
  },
  {
    q: "Are spare parts easily available?",
    a: "Yes.\n\nLIFTILE maintains ready inventory of genuine LT Series spare parts including replacement wheels, tyres, bearings, handles, axles and hardware. Contact our spares team to source what you need for your machine.",
  },
  {
    q: "Does LIFTILE provide a warranty on the LT Series?",
    a: "Yes.\n\nEvery LIFTILE LT Series Wheel Barrow is backed by our standard manufacturer's warranty on structural components. Contact our sales team for the current warranty terms applicable to your project and region.",
  },
  {
    q: "Is service and support available across India?",
    a: "Yes.\n\nLIFTILE provides spare parts, technical assistance and after-sales support across India through our nationwide dealer and support network.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact LML250 mini material lifter — perfect vertical companion to the LT Series barrow for slab-to-slab handling.",
    image: "/product-monkey-lift.jpg",
  },
  {
    slug: "concrete-mixer",
    name: "Concrete Mixer",
    desc: "LCM Series heavy-duty tilting drum mixer for residential, commercial and small-scale contractor concrete production.",
    image: "/product-concrete-mixer.jpg",
  },
  {
    slug: "reversible-drum-concrete-mixer",
    name: "Reversible Drum Concrete Mixer",
    desc: "LRDM Series high-output hydraulic reversible drum mixer for infrastructure, high-rise and precast projects.",
    image: "/product-reversible-drum-mixer.jpg",
  },
];

const CTA_BADGES = [
  "Heavy Duty Construction",
  "Premium Branded Tires",
  "Owner Inspected",
  "Made in India",
];

/* ---------- Blueprint SVG for Download Brochure ---------- */
const LT_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    {/* Ground line */}
    <line
      x1="20"
      y1="345"
      x2="385"
      y2="345"
      strokeDasharray="4 4"
      opacity="0.5"
    />

    {/* Tray body (side profile — trapezoidal barrow tray) */}
    <path d="M 130 150 L 320 170 L 305 245 L 145 240 Z" strokeWidth="1.6" />
    {/* Tray front lip */}
    <line x1="320" y1="170" x2="335" y2="190" opacity="0.7" />
    <line x1="305" y1="245" x2="320" y2="260" opacity="0.7" />
    <line x1="335" y1="190" x2="320" y2="260" opacity="0.7" />
    {/* Tray rim (top) */}
    <line
      x1="130"
      y1="150"
      x2="320"
      y2="170"
      strokeDasharray="2 2"
      opacity="0.55"
    />
    {/* Interior contour lines */}
    <path
      d="M 145 195 L 305 210"
      strokeDasharray="2 3"
      opacity="0.4"
    />
    <path
      d="M 148 215 L 302 227"
      strokeDasharray="2 3"
      opacity="0.35"
    />

    {/* Handles (twin, extending to left) */}
    <line x1="130" y1="180" x2="35" y2="215" strokeWidth="2" />
    <line x1="145" y1="225" x2="35" y2="245" strokeWidth="2" />
    {/* Handle grip wraps */}
    <rect
      x="25"
      y="205"
      width="25"
      height="18"
      strokeDasharray="2 2"
      opacity="0.65"
    />
    <rect
      x="25"
      y="238"
      width="25"
      height="18"
      strokeDasharray="2 2"
      opacity="0.65"
    />
    {/* Grip end caps */}
    <circle cx="25" cy="214" r="4" opacity="0.55" />
    <circle cx="25" cy="247" r="4" opacity="0.55" />

    {/* Front leg / stand */}
    <line x1="135" y1="240" x2="115" y2="345" strokeWidth="2" />
    <line x1="150" y1="240" x2="170" y2="345" strokeWidth="2" />
    {/* Cross brace on legs */}
    <line
      x1="120"
      y1="320"
      x2="165"
      y2="320"
      strokeDasharray="2 3"
      opacity="0.55"
    />

    {/* Front wheel (large, off the tray front) */}
    <circle cx="315" cy="320" r="26" strokeWidth="1.6" />
    <circle cx="315" cy="320" r="10" />
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const a = (i * Math.PI) / 3;
      return (
        <line
          key={i}
          x1={315 + Math.cos(a) * 10}
          y1={320 + Math.sin(a) * 10}
          x2={315 + Math.cos(a) * 26}
          y2={320 + Math.sin(a) * 26}
          opacity="0.55"
        />
      );
    })}
    {/* Tread pattern hint */}
    <circle cx="315" cy="320" r="26" strokeDasharray="3 3" opacity="0.35" />
    {/* Axle bracket from tray to wheel */}
    <line x1="320" y1="260" x2="315" y2="298" strokeWidth="2" />

    {/* Dimension callout for capacity */}
    <line
      x1="130"
      y1="120"
      x2="335"
      y2="140"
      strokeDasharray="3 3"
      opacity="0.55"
    />
    <line x1="130" y1="115" x2="130" y2="125" opacity="0.6" />
    <line x1="335" y1="135" x2="335" y2="145" opacity="0.6" />
    <text
      x="205"
      y="115"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.7"
    >
      3.5 cu.ft
    </text>

    {/* Component labels */}
    <text
      x="30"
      y="200"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      GRIP
    </text>
    <text
      x="215"
      y="200"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      HEAVY DUTY TRAY
    </text>
    <text
      x="290"
      y="365"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      PREMIUM WHEEL
    </text>
    <text
      x="120"
      y="365"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.65"
    >
      STAND
    </text>
  </svg>
);

export default function WheelBarrowPage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Wheel Barrow (LT Series)" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lt-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LT Series"
        title={
          <>
            LIFTILE Wheel
            <br />
            <span className="text-liftile-orange">Barrow</span>
          </>
        }
        subtitle="Engineered for efficient material transportation across construction sites. Built with heavy-duty construction, premium branded wheels and ergonomic handling to deliver dependable, everyday performance in demanding site conditions."
        models={Object.keys(MODELS)}
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lt-gallery"
        heading={
          <>
            LT Series <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at the LIFTILE LT Series Wheel Barrow — from precision fabrication at our factory to real construction sites across India."
        items={GALLERY}
        seriesLabel="LT Series"
        imageAlt="LIFTILE LT Series Wheel Barrow"
      />

      {/* ─── SECTION 3: QUICK SPECS (split layout with 3 model tabs) ─── */}
      <QuickSpecsTabs
        testId="lt-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="The essentials — with a model selector to switch between LT1X, LT2X and LT3X configurations."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LT2X"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lt-features"
        heading={
          <>
            Built for Daily Site Use.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Everyday Reliability.
            </span>
          </>
        }
        subtitle="Every LIFTILE LT Series Wheel Barrow is engineered with heavy-duty steel construction, premium branded wheels and rigorous quality inspections to deliver dependable performance on demanding construction sites."
        items={FEATURES}
      />

      {/* ─── SECTION 5: APPLICATIONS ─── */}
      <Applications
        testId="lt-applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">Construction Site</span>
          </>
        }
        subtitle="From residential builds to warehouses, road construction and landscaping — the LT Series delivers dependable everyday material handling across every construction environment."
        items={APPLICATIONS}
        variant="dark"
        gridCols="lg:grid-cols-4"
      />

      {/* ─── SECTION 6: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lt-why-choose"
        image="/product-wheel-barrow.jpg"
        imageAlt="LIFTILE LT Series Wheel Barrow — heavy-duty everyday material handling"
        imageCaption="LT Series — heavy-duty build, premium wheels, dependable everyday site performance."
        floatingBadge={{ top: "3.5", bottom: "Cubic Feet" }}
        eyebrow="Why LT Series"
        heading={
          <>
            Built for Everyday{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Site Material Handling
            </span>
          </>
        }
        subtitle="The LT Series combines heavy-duty steel construction, premium branded wheels and ergonomic handling to deliver dependable everyday material transport on every construction project."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 7: TECHNICAL HIGHLIGHTS ─── */}
      <QualitySafetyCommitment
        testId="lt-tech-highlights"
        eyebrow="Technical Highlights"
        heading={
          <>
            Precision Engineering.{" "}
            <span className="text-liftile-orange">
              Industrial Grade Build.
            </span>
          </>
        }
        subtitle="A closer look at the engineering foundations that make the LT Series a heavy-duty, dependable and long-lasting wheel barrow for modern construction sites."
        items={TECH_HIGHLIGHTS}
      />

      {/* ─── SECTION 8: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lt-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete LT Series technical catalogue including specifications, drawings, features and product information for LT1X, LT2X and LT3X configurations."
        blueprintSvg={LT_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 9: FAQ ─── */}
      <PremiumFAQ
        testId="lt-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders, contractors and site engineers most often ask us about the LIFTILE LT Series Wheel Barrow."
        items={FAQS}
      />

      {/* ─── SECTION 10: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lt-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 11: FINAL CTA ─── */}
      <ProductFinalCTA
        testId="lt-product-cta"
        backgroundImage="/project-kota.png"
        backgroundAlt="LIFTILE LT Series Wheel Barrow on a live construction site"
        heading={
          <>
            Move Materials with{" "}
            <span className="text-liftile-orange">Confidence.</span>
          </>
        }
        subtitle="The LIFTILE LT Series Wheel Barrows are engineered for dependable material handling, superior durability and everyday construction performance."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Contact Sales Team", href: "/contact" }}
        trustBadges={CTA_BADGES}
      />

      <Footer />
    </>
  );
}
